import { createContext, useContext, useMemo, useReducer, ReactNode } from "react"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"
import { useContentSetsMetadata } from "resources/content-sets"

export interface ContentSetCategory {
  id: string
  name: string
}

export interface ContentSetFactor {
  id: string
  name: string
  category: string
}

export interface ContentSetMetadata {
  id: string
  name: string
  proprietary?: boolean
  factors?: ContentSetFactor[]
  categories?: ContentSetCategory[]
}

const PREFERENCE_SUBTYPE = "graph-content-set"
const PREFERENCE_FIELDS = ["selectedCategory"]

interface ContentSetState {
  selectedCategory: string
}

const DEFAULT_STATE: ContentSetState = {
  selectedCategory: "total",
}

type ContentSetAction =
  | { type: "setSelectedCategory", selectedCategory: string }
  | { type: "setPreferences", preferences: Partial<ContentSetState> | null }

function reducer(current: ContentSetState, action: ContentSetAction): ContentSetState {
  return produce(current, draft => {
    switch (action.type) {
      case "setSelectedCategory":
        draft.selectedCategory = action.selectedCategory
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
    }
  })
}

function setPreferences(preferences: Partial<ContentSetState>): ContentSetAction {
  return { type: "setPreferences", preferences }
}

export interface ContentSetContextValue extends ContentSetState {
  isPreferencesLoaded: boolean
  contentSetsMetadata: ContentSetMetadata[] | null
  contentSetsStatus: string
  setSelectedCategory: (selectedCategory: string) => void
}

export const CONTENT_SET_CONTEXT = createContext<ContentSetContextValue | undefined>(undefined)

export function useContentSetContext(): ContentSetContextValue {
  const ctx = useContext(CONTENT_SET_CONTEXT)
  if (ctx === undefined) {
    throw new Error("useContentSetContext must be used within a ContentSetContext provider")
  }
  return ctx
}

interface ContentSetContextProps {
  children: ReactNode
}

export default function ContentSetContext({ children }: ContentSetContextProps) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)
  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const [contentSetsMetadata, contentSetsStatus] = useContentSetsMetadata() as [ContentSetMetadata[] | null, string]

  const contextUpdaters = useMemo(() => ({
    setSelectedCategory: (selectedCategory: string) => dispatch({ type: "setSelectedCategory", selectedCategory }),
  }), [dispatch])

  const contextValue = useMemo((): ContentSetContextValue => ({
    ...state,
    ...contextUpdaters,
    isPreferencesLoaded,
    contentSetsMetadata,
    contentSetsStatus,
  }), [state, contextUpdaters, isPreferencesLoaded, contentSetsMetadata, contentSetsStatus])

  return <CONTENT_SET_CONTEXT.Provider value={contextValue}>
    {children}
  </CONTENT_SET_CONTEXT.Provider>
}
